import React, { useState, useEffect } from "react";
import { apiRequest } from "../utils/api";
import { useAuthContext } from "../context/AuthContext";
import { useAlert } from '../components/AlertModal';
import { useLocation, useNavigate } from "react-router-dom";
import AddProductForm from "../components/AddProductForm";
import SearchForm from "../components/SearchForm";
import useInventoryViewStore from "../stores/useInventoryViewStore";
import "../styles/InventoryPage.css";

function Inventory() {
  const [storeId, setStoreId] = useState("");
  const [includePriceLink, setIncludePriceLink] = useState(true);
  const [status, setStatus] = useState('UPLOAD');
  const { user: userInfo, loading } = useAuthContext();
  const { showAlert } = useAlert();
  const [soldWithinYear, setSoldWithinYear] = useState(false);
  const CATEGORY_CYCLE = [1, 2, 3, 4];
  const [categoryIndex, setCategoryIndex] = useState(0);
  const currentCategoryId = CATEGORY_CYCLE[categoryIndex];
  const [pageResetKey] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [buttonComplete, setButtonComplete] = useState(false);

  // Zustand 管理状態
  const {
    actualShowPriceLabel,
    requestShowPriceLabel,
    setActualShowPriceLabel,
    clearRequestShowPriceLabel,
  } = useInventoryViewStore();

  useEffect(() => {
    if (userInfo?.store_id) {
      setStoreId(userInfo.store_id);
    }
  }, [userInfo]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // 在庫0
    if (params.has("sold_within_year")) {
      setSoldWithinYear(params.get("sold_within_year") === "true");
    }

    // カテゴリー
    if (params.has("category_id")) {
      const idx = CATEGORY_CYCLE.indexOf(Number(params.get("category_id")));
      if (idx !== -1) {
        setCategoryIndex(idx);
      }
    }
  }, [location.search]);

  
  // アップロード進捗復元
  useEffect(() => {
    const savedTaskId = localStorage.getItem("exportTaskId");
    if (!savedTaskId) return;

    setStatus("Uploading...");
    setButtonComplete(false);

    const interval = setInterval(async () => {
      const progressRes = await apiRequest(
        `inventory/export/all/progress/${savedTaskId}/`,
        "GET"
      );

      console.log("progressRes:", progressRes);

      const currentProgress = progressRes.progress;
      setProgress(currentProgress);

      if (currentProgress === 100) {
        clearInterval(interval);
        localStorage.removeItem("exportTaskId");
        setButtonComplete(true);
        setStatus("UPLOAD");
      }

      if (currentProgress === -1) {
        clearInterval(interval);
        localStorage.removeItem("exportTaskId");
        setStatus("UPLOAD");

        if (progressRes.errors?.length) {
          const text = progressRes.errors
            .map(err => `ID:${err.inventory_id} / ${err.item_name} / JAN:${err.jan}\n${err.error}`)
            .join("\n\n");

          showAlert(`${progressRes.message || "アップロード中にエラーが発生しました"}\n\n${text}`);
        } else {
          showAlert(progressRes.message || "アップロード中にエラーが発生しました");
        }
      }
    }, 1000);

  return () => clearInterval(interval);
}, []);
    

  if (loading) return <div>読み込み中...</div>;
    const registerName = userInfo.register_name;

  const handleUploadAll = async () => {
    console.log("🔥 handleUploadAll START");
    setStatus("Uploading...");
    setProgress(0);
    setButtonComplete(false);

    try {
      console.log("📤 calling start API...");
      const startRes = await apiRequest(
        "inventory/export/all/start/",
        "POST",
        { include_price_link: includePriceLink }
      );
      console.log("✅ startRes:", startRes);

      const taskId = startRes.task_id;
      console.log("🆔 taskId:", taskId);

      if (!taskId) {
        showAlert("タスク開始に失敗しました");
        setStatus("UPLOAD");
        return;
      }

      localStorage.setItem("exportTaskId", taskId);

      const interval = setInterval(async () => {
        console.log("⏳ polling progress...", taskId);

        const progressRes = await apiRequest(
          `inventory/export/all/progress/${taskId}/`,
          "GET"
        );

        console.log("📥 progressRes:", progressRes);

        const currentProgress = progressRes.progress;
        console.log("📊 progress:", currentProgress);

        setProgress(currentProgress);

        if (currentProgress === 100) {
          clearInterval(interval);
          setButtonComplete(true);
          setStatus("UPLOAD");
          showAlert("すべてのアップロードが完了しました！");
        }

        if (currentProgress === -1) {
          clearInterval(interval);
          setStatus("UPLOAD");
          showAlert(progressRes.message || "アップロード中にエラーが発生しました");
        }
      }, 1000);

    } catch (err) {
      console.error("❌ handleUploadAll error:", err);
      console.log("❌ error.details:", err?.details);
      setStatus("再UPLOAD");
      showAlert("通信エラーが発生しました");
    }
  };


  const handleToggleSoldWithinYear = () => {
    const next = !soldWithinYear;
    setSoldWithinYear(next);

    const params = new URLSearchParams(location.search);
    params.set("sold_within_year", next);
    params.set("category_id", currentCategoryId);

    navigate(`/inventory_list?${params.toString()}`);
  };

  // カテゴリー切り替え関数
  const handleCycleCategory = () => {
    const nextIndex = (categoryIndex + 1) % CATEGORY_CYCLE.length;
    const nextCategoryId = CATEGORY_CYCLE[nextIndex];

    setCategoryIndex(nextIndex);

    const params = new URLSearchParams(location.search);

    params.set("category_id", nextCategoryId);
    params.set("page", 1); 
    params.set("sold_within_year", soldWithinYear);

    navigate(`/inventory_list?${params.toString()}`);
  };

  const handleToggleSpecialPrice = () => {
    const params = new URLSearchParams(location.search);

    const isOn = params.get("buy_specialprice_operator") === "gt";

    if (isOn) {
      params.delete("buy_specialprice");
      params.delete("buy_specialprice_operator");
      params.delete("buy_specialprice_include_null");
    } else {
      params.set("buy_specialprice", -99999);
      params.set("buy_specialprice_operator", "gt");
      params.set("buy_specialprice_include_null", "true");
    }

    params.set("page", 1);
    navigate(`/inventory_list?${params.toString()}`);
  };
  
  return (
    <>
      <div className="InventoryPage-header">
        <AddProductForm />
        <div className="InventoryPage-controls">
          <div className="InventoryPage-toggle-wrapper">
            <div className="InventoryPage-segment">
              <button
                className={`InventoryPage-segment-button ${!actualShowPriceLabel ? 'active' : ''}`}
                onClick={() => {
                  setActualShowPriceLabel(false);
                  setTimeout(clearRequestShowPriceLabel, 100);
                }}
              >
                検索
              </button>
              <button
                className={`InventoryPage-segment-button ${actualShowPriceLabel ? 'active' : ''}`}
                onClick={() => {
                  setActualShowPriceLabel(true);
                  setTimeout(clearRequestShowPriceLabel, 100);
                }}
              >
                ラベル
              </button>
            </div>
            <div className="InventoryPage-segment2">
              <button
                type="button"
                className={`InventoryPage-segment-button2 ${soldWithinYear ? 'active' : ''}`}
                onClick={() => { 
                  handleToggleSoldWithinYear();
                  setActualShowPriceLabel(false);
                  setTimeout(clearRequestShowPriceLabel, 100);
                }}
              >
                在庫0
              </button>
              <button
                type="button"
                className="search-filter-button"
                onClick={handleCycleCategory}
              >
                CAT：{currentCategoryId}
              </button>
              <button
                type="button"
                className={`search-filter-button2 ${
                  new URLSearchParams(location.search).get("buy_specialprice_operator") === "gt"
                    ? "active"
                    : ""
                }`}
                onClick={handleToggleSpecialPrice}
              >
                要
              </button>
            </div>
          </div>
          <label className="InventoryPage-toggle">
            <input
              type="checkbox"
              checked={includePriceLink}
              onChange={() => setIncludePriceLink(!includePriceLink)}
            />
            <span className="InventoryPage-toggle-slider"></span>
            <span className="InventoryPage-toggle-label">最新価格ASK</span>
          </label>
          <button
            className={
              status === "Uploading..."
                ? `InventoryPage-uploadingButton ${buttonComplete ? "complete" : ""}`
                : "InventoryPage-uploadButton"
            }
            style={
              status === "Uploading..."
                ? { "--progress-width": `${progress}%` }
                : {}
            }
            onClick={handleUploadAll}
          >
            <span>{status}</span>
          </button>
        </div>
      </div>

      {actualShowPriceLabel ? (
        <SearchForm
          storeId={storeId}
          requestShowPriceLabel={requestShowPriceLabel}
          onShowPriceLabelChange={setActualShowPriceLabel}
          registerName={registerName}
          soldWithinYear={soldWithinYear}
          onToggleSoldWithinYear={() => setSoldWithinYear(prev => !prev)}
          categoryId={currentCategoryId}
        />
      ) : (
        <>
          <SearchForm
            storeId={storeId}
            requestShowPriceLabel={requestShowPriceLabel}
            onShowPriceLabelChange={setActualShowPriceLabel}
            soldWithinYear={soldWithinYear}
            onToggleSoldWithinYear={() => setSoldWithinYear(prev => !prev)}
            categoryId={currentCategoryId}
            pageResetKey={pageResetKey}
          />
        </>
      )}
    </>
  );
}

export default Inventory;
